// $FlowFixMe
import React, {
  useCallback,
  useEffect,
  useState,
  useContext,
  useMemo,
} from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Row } from 'react-bootstrap';
import {
  reduxForm,
  getFormSyncWarnings,
  getFormValues,
  getFormSyncErrors,
  getFormAsyncErrors,
} from 'redux-form';
import { pathOr, isNil, curryN, isEmpty } from 'ramda';
import { withLDConsumer } from 'launchdarkly-react-client-sdk';
import { useModal } from '@fortress-technology-solutions/fortress-component-library/Molecules_Fortress';

import {
  canGenerateLease,
  getLeaseDifference,
  getNonRentRecurringCharges,
  getTotalMonthlyCharges,
  parseRentValues,
  getMonthlyTransactions,
} from '../../../utils/lease-helpers.js';
import type { GlobalState } from '../../App/types';

import LeaseRentAmountModal from './LeaseRentAmountModal/index.js';
import EditLeaseDataModal from './EditLeaseDataModal';
import EditRequiredLeaseFeesModal from './LeaseDataTabFormSections/EditRequiredLeaseFeesModal';
import { useAsyncEditMonthlyRequiredFees } from '../hooks';
import { promptToaster } from '../../../containers/App/actions';
import { getOneResident } from '../../ResidentProfile/actions';
import { getOneApplication } from '../../ApplicationProfile/actions';
import type { Props } from './types';
import validate from './validate';
import warn from './warn';
import { asyncValidate } from './asyncValidate';
import { withExpirationLimitsPropertyToggle } from '../../../hooks/data-fetching/useExpirationLimitsPropertyToggle';

/* Form Sections - In Order of Appearance */
import LeaseBasics from './LeaseDataTabFormSections/LeaseBasics';
import MonthlyLeaseCharges from './LeaseDataTabFormSections/MonthlyLeaseCharges';
import Reconciliation from './LeaseDataTabFormSections/Reconciliation';
import PetFeesAndRent from './LeaseDataTabFormSections/PetFeesAndRent';
import OneTimeLeaseChargesOrCredits from './LeaseDataTabFormSections/OneTimeLeaseCharges';
import Concessions from './LeaseDataTabFormSections/Concessions';
import EmergencyContactInformation from './LeaseDataTabFormSections/EmergencyContactInformation';
import MonthlyLeaseChargesOrCredits from './LeaseDataTabFormSections/MonthlyLeaseChargesOrCredits';
import LeaseSigningMethod from './LeaseDataTabFormSections/LeaseSigningMethod';
import LeaseActionButtons from './LeaseDataTabFormSections/LeaseActionButtons';

import {
  getIsSection236,
  getAffordableFloorPlanProgramTypes,
} from '../../../utils/affordable.js';
import LeasePacket from './LeaseDataTabFormSections/LeasePacket/index.js';
import { useQueryClient } from 'react-query';

import { LeaseDataTabContext } from '../context';
import useHasPermission from '../../../hooks/useHasPermission.js';
import {
  useProratedOtherCalculation,
  useRecommendedLeaseRent,
  useLeaseProviderState,
} from './hooks';
import { useTransferLeaseCertGate, buildMoveInCertTooltip } from '../hooks';
import { recalculateProratedAmountsForRentChange } from './LeaseDataTabFormSections/utils';

const getCompleteLeaseTermList = (
  leaseTermId: string,
  leaseTerms: Object[],
  softDeletedLeaseTerms: Object[] = [],
): Object[] => {
  const foundLeaseTerm = softDeletedLeaseTerms
    ? softDeletedLeaseTerms.find((lt) => lt.id === leaseTermId)
    : null;
  const isSelectedLeaseTermSoftDeleted = !isNil(foundLeaseTerm);

  return isSelectedLeaseTermSoftDeleted
    ? leaseTerms.concat({ ...foundLeaseTerm, disabled: true })
    : leaseTerms;
};

export const LeaseDataTabForm = (props: Props) => {
  const { open, openModal, closeModal } = useModal(false);
  const [modal, setModal] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [pendingMoveInCheckState, setPendingMoveInCheckState] = useState({
    isChecking: false,
    hasError: false,
  });
  const showLeaseDataModal = () => setModal('editLeaseDataModal');
  const showLeaseReqFeesModal = () => setModal('editLeaseRequiredFeesModal');
  const hideModals = () => setModal(null);

  const {
    actions,
    affordableValues,
    applicableRents,
    utilityAllowanceId,
    affordableFloorplanPricing,
    allAffordableTabsApproved,
    unapprovedAffordablePrograms = [],
    applicationId,
    moveInCertsApproved = true,
    unapprovedMoveInPrograms = [],
    currentLeaseEndDate,
    basicLeaseFees,
    dirty,
    change,
    formSyncWarnings,
    formValues,
    frNames,
    handleSubmit,
    hasNonFR,
    householdId,
    intl,
    isDisabledMonthly,
    isEditable,
    labelEndDate,
    lateMethods,
    lease,
    leaseRentPercentage,
    leaseSignatureStatuses,
    leaseTerms,
    monthlyRecurringCharges,
    onGenerate,
    onSubmit,
    prospectInfo,
    residentId,
    securityDeposits,
    selectedMonthlyOption,
    selectedProperty,
    setFormDirty,
    transferInformation,
    unit,
    updateLeaseDate,
    valid,
    touch,
    softDeletedLeaseTerms,
    editFormValues,
    editLeaseFeesFormValues,
    updateRequiredFeesList,
    requiredFeesUpdated,
    asyncValidating,
    formAsyncErrors,
    formSyncErrors,
    noticeToVacate,
    flags,
    setProratedRentAmount,
    setProratedOtherAmount,
    resetProratedRentAutoFilledChangeStatus,
    handleAutoFilledFieldChange,
    autoFilledLeaseChargeFields,
  } = props;

  /**
   * We could move the ApplicantHeader and ResidentHeader components into the
   * LeaseDataTabForm folder, and have them called in this file in order to
   * avoid the need for this useEffect and to get rid of the `formDirty` state
   * in the parent component.
   */
  useEffect(() => {
    let subscribed = true;
    if (subscribed) {
      setFormDirty(dirty);
      if (!isEmpty(formSyncErrors)) {
        // Touch the error fields so they display the sync errors on load.
        touch('FormLeaseDataTab', Object.keys(formSyncErrors));
      }
    }
    return () => {
      subscribed = false;
    };
    // eslint-disable-next-line
  }, [dirty, setFormDirty]);

  const restoreSigMethod = useCallback(
    (method) => change('desiredSignatureMethod', method),
    [change],
  );
  // An unsaved Blue Moon choice can outlive the flag, so once the flag was on in this session the
  // revert stays wired. A session that never had the flag on gets no revert.
  const [blueMoonSeen, setBlueMoonSeen] = useState(
    Boolean(flags?.blueMoonLeaseSigning),
  );
  useEffect(() => {
    if (flags?.blueMoonLeaseSigning) setBlueMoonSeen(true);
  }, [flags?.blueMoonLeaseSigning]);
  const queryClient = useQueryClient();
  const {
    leaseProvider,
    providerIsUsable,
    shouldMountLeasePacket,
    shouldDisplayLeasePacket,
    isLeasePacketReadOnly,
  } = useLeaseProviderState({ flags, selectedProperty, lease });
  const section236Flag = flags?.section236;
  const floorPlan = unit?.floorPlan;
  const isSection236 =
    section236Flag && getIsSection236(selectedProperty, floorPlan);
  const { isHUDFloorPlan, isRDFloorPlan, isPHFloorPlan } =
    getAffordableFloorPlanProgramTypes(floorPlan);
  const {
    marketRent,
    quotingRent,
    basicRent,
    noteRent,
    hud236BasicRent,
    hud236MarketRent,
  } = parseRentValues(affordableValues, unit, lease, isSection236, flags);
  const nonOptionalChargessFeature = flags?.nonOptionalChargessFeature ?? false;
  const nonOptionalCharge = nonOptionalChargessFeature
    ? affordableValues?.nonOptionalCharge
    : 0;
  const {
    submitUpdateMonthlyRequiredFees,
    isSubmitting: isSubmittingUpdateFees,
  } = useAsyncEditMonthlyRequiredFees(
    pathOr('', ['organizationId'], selectedProperty),
    pathOr('', ['id'], selectedProperty),
    pathOr('', ['id'], lease),
    updateRequiredFeesList,
    actions.promptToaster,
  );
  const refreshData = () => {
    queryClient.invalidateQueries([
      'household',
      'lease',
      'validateTransferLease',
    ]);
  };
  const handleFormSubmit = (submitParams: Object) => {
    setSubmitting(true);
    // The save is a fire-and-forget saga; the authoritative post-save state comes from the
    // getOneResident/getOneApplication refetch it triggers, which re-seeds the form through
    // `enableReinitialize` from the persisted record. We deliberately do NOT re-initialize from
    // the submit-time values here: those are the pre-mapping form values, and mapFormValuesLeaseData
    // drops empty/'default' fields before sending, so a snapshot baseline could both revert edits
    // typed during the round trip and mark a never-persisted value as saved. The settle callback
    // only clears the in-flight flag; a failed save then leaves the form dirty (no refetch), so it
    // is never shown as saved. (ENG-32170)
    onSubmit(submitParams, refreshData, () => setSubmitting(false));
  };

  const handleLeaseDataModalSubmit = (editValues: Object) => {
    const { formValues } = props;
    const updatedLease = {
      ...formValues,
      startDate: editValues.editLeaseStartDate,
      endDate: editValues.editLeaseEndDate,
      leaseTermId: editValues.editLeaseTerm,
      leasedRent: editValues.editLeaseRentAmount,
      isReceivingAssistance: editValues.editIsReceivingAssistance,
      overrideLeaseExpirationLimit: editValues.overrideLeaseExpirationLimit,
    };

    // ENG-31259 — this modal's Save bypasses the tracked <Field>s in LeaseBasics/rightColumn.js
    // entirely (it has its own editLeaseTerm/editLeaseStartDate fields, submitted straight to
    // onSubmit), so without this nothing on this path ever calls notifyTrackedInputChanged and a
    // term/date change made here never recalculates. The modal has no Move-In Date field of its
    // own (only Lease Term and Lease Start Date), so those are the two tracked inputs it can move.
    // Reuses the same hook API a real inline edit uses rather than duplicating pricing logic here.
    const changedLeaseTerm =
      editValues.editLeaseTerm !== formValues?.leaseTermId;
    const changedStartDate =
      editValues.editLeaseStartDate !== formValues?.startDate;
    if (changedLeaseTerm || changedStartDate) {
      // The rent being submitted here (editValues.editLeaseRentAmount) was set for the OLD
      // term/dates — clear whatever recommendation is pending or displayed for those before
      // arming a fresh one, so a later-arriving stale price can't silently overwrite what's about
      // to be saved, and the "Recommended Rent" label can't keep showing a figure that no longer
      // matches it.
      clearRecommendedRent();
      notifyTrackedInputChanged();
    }

    hideModals();
    onSubmit(updatedLease);
  };

  const handleReceivingAsstSubmit = (value: boolean) => {
    const { formValues } = props;
    const updatedLease = {
      ...formValues,
      isReceivingAssistance: value,
    };

    onSubmit(updatedLease);
  };
  const handleLeaseRequiredFeesSubmit = async (feesToRemove: Array<Object>) => {
    const { monthlyReqFees } = editLeaseFeesFormValues;
    const parseFormRequiredFee = (reqFee, removed) => {
      const isNumerical = reqFee.type === 'numeric';
      return {
        id: reqFee.id,
        ptc: reqFee.ptc,
        leaseFeeAmount: isNumerical ? Number(reqFee.value) : 0,
        descriptiveFeeAmount: isNumerical ? null : reqFee.descriptiveValue,
        isRemoved: removed,
      };
    };
    const editedFees = monthlyReqFees.map((fee) =>
      parseFormRequiredFee(fee, false),
    );
    const removedFees = feesToRemove.map((fee) =>
      parseFormRequiredFee(fee, true),
    );
    const cancelProratedOtherRecalculation = onRequiredChargesSave();
    const saved = await submitUpdateMonthlyRequiredFees({
      removedMonthlyFees: removedFees,
      requiredMonthlyFees: editedFees,
    });
    if (!saved) {
      cancelProratedOtherRecalculation();
    }

    hideModals();
  };
  const handleLeaseRequiredFeesSubmitCurried = curryN(
    2,
    handleLeaseRequiredFeesSubmit,
  );
  const { allMonthlyTransactions, updateTransactionsList } =
    useContext(LeaseDataTabContext);

  const monthlyTransactions = getMonthlyTransactions(
    allMonthlyTransactions,
    selectedMonthlyOption,
  );

  const totalMonthlyCharges = useMemo(() => {
    return getTotalMonthlyCharges(allMonthlyTransactions, lease);
  }, [allMonthlyTransactions, lease]);

  const activeMonthlyRecurringFees = monthlyRecurringCharges.filter(
    (fee) => !fee.isRemoved,
  );

  const nonRentCharges = getNonRentRecurringCharges(activeMonthlyRecurringFees);

  const applicableNonRentCharges = nonRentCharges;
  const difference = getLeaseDifference(
    pathOr(0, ['leasedRent'], formValues),
    applicableNonRentCharges,
    totalMonthlyCharges,
  );

  const currentLeaseSelected = residentId && pathOr(null, ['isMovedIn'], lease);

  const editLeaseDataModalInitial = {
    editLeaseTerm: pathOr(null, ['leaseTermId'], formValues),
    editLeaseStartDate: pathOr(null, ['startDate'], formValues),
    editLeaseEndDate: pathOr(null, ['endDate'], formValues),
    editLeaseRentAmount: pathOr(null, ['leasedRent'], formValues),
    editIsReceivingAssistance: pathOr(
      null,
      ['isReceivingAssistance'],
      formValues,
    ),
    overrideLeaseExpirationLimit: !isNil(
      formValues?.overrideLeaseExpirationLimitUpdatedAt,
    ),
  };
  const editLeaseRequiredFeesInitial = monthlyRecurringCharges.map(
    (fee, idx) => ({
      ptc: fee.ptc,
      type: isNil(fee.descriptiveValue) ? 'numeric' : 'descriptive',
      descriptiveValue: fee.descriptiveValue,
      value: fee.value,
      id: fee.id,
      isRemoved: fee.isRemoved,
      idx,
      label: fee.label,
    }),
  );

  const canComplete = canGenerateLease(formSyncWarnings, valid);
  const isRenewalComplete = pathOr(false, ['isRenewalComplete'], lease);
  const receivingAssistance = pathOr(
    null,
    ['isReceivingAssistance'],
    formValues,
  );

  const leaseBasicsLeaseTerms = getCompleteLeaseTermList(
    pathOr('', ['leaseTermId'], formValues),
    leaseTerms,
    softDeletedLeaseTerms,
  );
  const editModalLeaseTerms = getCompleteLeaseTermList(
    pathOr('', ['editLeaseTerm'], editFormValues),
    leaseTerms,
    softDeletedLeaseTerms,
  );
  const hasAutoPopulatedFieldsEditPermission = useHasPermission(
    'auto-lease-fields-edit',
  );
  const autoPopulatedFieldsAreEditable = !flags.automatedLeaseChargesP1
    ? isEditable
    : isEditable && hasAutoPopulatedFieldsEditPermission;

  const {
    householdRentableItems,
    petCount,
    isShortTermLease,
    onRequiredChargesSave,
  } = useProratedOtherCalculation({
    flags,
    selectedProperty,
    householdId,
    formValues,
    leaseTerms,
    leaseId: lease?.id,
    monthlyRecurringCharges,
    setProratedOtherAmount,
    change,
    resetProratedRentAutoFilledChangeStatus,
  });

  const { isBlocked: transferCertBlocked, tooltipTitle: transferCertTooltip } =
    useTransferLeaseCertGate({ lease, householdId, selectedProperty, intl });

  // ENG-32187 / ENG-32886 — the applicant/move-in gate, built here (like the transfer gate above)
  // so the two method-button components consume a ready tooltip string. It blocks generate +
  // execute (never Save) when an active MOVE_IN cert is unapproved; `moveInCertsApproved` /
  // `unapprovedMoveInPrograms` are the tab-scoped values from ApplicationProfile.
  const moveInCertBlocked = !moveInCertsApproved;
  const moveInCertTooltip = moveInCertBlocked
    ? buildMoveInCertTooltip(intl, unapprovedMoveInPrograms)
    : '';

  // ENG-30333 — recommend (and, on save, persist) a lease rent from REBA's pricing whenever the
  // unit, move-in date, start date, or lease term is EDITED by the user while the lease is still
  // editable. See useRecommendedLeaseRent's own doc comment for why this is event-driven —
  // `notifyTrackedInputChanged` (destructured below and threaded down to LeaseBasics ->
  // rightColumn.js's tracked `<Field>`s) is the only thing that can arm a recalculation; nothing
  // here (or in the hook) infers an edit by comparing values across renders. That's what makes
  // opening/reopening the tab and switching leases in the header selector (Scenario 3/4) safe,
  // and it never runs once the lease is locked (Scenario 5).
  //
  // FINDING 4 — moveInDate and startDate are passed through SEPARATELY, not pre-merged into a
  // single value here. Merging them (`formValues?.startDate ?? formValues?.moveInDate`) before
  // calling the hook collapses two distinct tracked inputs into one: whichever field is non-null
  // wins permanently, so once startDate is set, editing Move-In Date can never change the merged
  // value and Scenario 2 silently breaks for that field. The hook itself tracks both and computes
  // the date actually sent to pricing (`startDate ?? moveInDate` — see its doc comment for why
  // that's the right one for a renewal) internally.
  // STEP 4 — an inline arrow here would be a brand-new function identity on EVERY render of this
  // component (for any reason at all, not just a tracked-input edit), which made the apply effect
  // inside useRecommendedLeaseRent (keyed on `onRecalculate`) re-run on every unrelated render too
  // — the arm/settle interaction ending up dependent on render timing rather than on its own
  // inputs. `useCallback` fixes that, but only if its dep list stays to the PRIMITIVE fields the
  // recompute actually reads (`startDate`/`monthlyPetRent`/`shortTermFee`) rather than the whole
  // `formValues` object, which — like every other field on this form — gets a new identity on
  // every keystroke of ANY field, tracked or not; depending on the whole object would silently
  // reintroduce the exact per-render churn this change exists to remove.
  const startDateForRecalc = formValues?.startDate;
  const monthlyPetRentForRecalc = formValues?.monthlyPetRent;
  const shortTermFeeForRecalc = formValues?.shortTermFee;

  const onRecalculateLeasedRent = useCallback(
    (amount) => {
      change('leasedRent', amount);
      // FINDING 7 — writing `leasedRent` via `change` does NOT invoke the <Field>'s onChange
      // handler (onChangeLeaseRentAmount, MonthlyLeaseCharges/index.js), which is the only place
      // `proratedRent`/`proratedOther` get recomputed from the rent. Without this, an automatic
      // recalculation would leave the prorated first-month charges derived from the PREVIOUS
      // rent — a real billing mismatch. Reuse the exact same recompute a manual edit performs,
      // gated the same way (automatedLeaseChargesP1), with the same startDate a manual edit on
      // this field would have used.
      if (flags?.automatedLeaseChargesP1) {
        recalculateProratedAmountsForRentChange({
          rentAmount: amount,
          startDate: startDateForRecalc,
          monthlyRecurringCharges,
          formValues: {
            monthlyPetRent: monthlyPetRentForRecalc,
            shortTermFee: shortTermFeeForRecalc,
          },
          petCount,
          isShortTermLease,
          householdRentableItems,
          setProratedRentAmount,
          setProratedOtherAmount,
          resetProratedRentAutoFilledChangeStatus,
          change,
        });
      }
    },
    [
      change,
      flags?.automatedLeaseChargesP1,
      startDateForRecalc,
      monthlyPetRentForRecalc,
      shortTermFeeForRecalc,
      monthlyRecurringCharges,
      petCount,
      isShortTermLease,
      householdRentableItems,
      setProratedRentAmount,
      setProratedOtherAmount,
      resetProratedRentAutoFilledChangeStatus,
    ],
  );

  const {
    recommendedRentProviderName,
    clearRecommendedRent,
    notifyTrackedInputChanged,
  } = useRecommendedLeaseRent({
    enabled: flags?.rebaRevMgmt,
    organizationId: selectedProperty?.organizationId,
    propertyId: selectedProperty?.id,
    leaseId: lease?.id,
    unitId: unit?.id,
    moveInDate: formValues?.moveInDate,
    startDate: formValues?.startDate,
    leaseTermId: formValues?.leaseTermId,
    // ENG-31245 — the same signal rightColumn.js already reads to disable the Move-In Date
    // <Field> for a renewal (that field holds the household's ORIGINAL move-in date there, not
    // this lease's effective date) — reused rather than a new flag.
    isRenewal: lease?.isRenewal,
    // ENG-30334 — the expiring lease's end date, so a renewal can be priced as of the day after
    // it ends while the renewal's own Lease Start Date is still empty. Derived in the container
    // (LeaseDataTab/index.js) from the same `leases[1]` it already calls "the previous lease".
    currentLeaseEndDate,
    isEditable,
    isLocked: lease?.isLocked,
    // FINDING 6 — the SAME condition getLeaseRentField (MonthlyLeaseCharges/index.js) uses to
    // decide the Lease Rent <Field>'s own `disabled` prop: `leaseRentIsEditable` passed to that
    // component below is `isEditable`, but an `isPH` floor plan hard-disables the field
    // regardless of that value (rent is program-set by formula there — see the `isPH` branch of
    // getLeaseRentField). Passing the re-derived condition through by name, rather than letting
    // the hook approximate it from isEditable alone, is what keeps the two from ever disagreeing.
    isLeaseRentEditable: isEditable && !isPHFloorPlan,
    // Read off `lease` rather than `formValues`: `lease` lands in the same render as `leaseId`,
    // while `formValues` arrives a commit later.
    persistedPricingSource: lease?.pricingSource ?? null,
    onRecalculate: onRecalculateLeasedRent,
  });

  return (
    <React.Fragment>
      {lease && (
        <LeaseRentAmountModal
          open={open}
          closeModal={closeModal}
          leaseRentPercentage={leaseRentPercentage}
          quotingRent={quotingRent}
          unit={unit}
          receivingAssistance={receivingAssistance}
          affordableValues={affordableValues}
          applicableRents={applicableRents}
          lease={lease}
          hud236BasicRent={isSection236 ? hud236BasicRent : null}
          hud236MarketRent={isSection236 ? hud236MarketRent : null}
          property={selectedProperty}
          intl={intl}
          affordableFloorplanPricing={affordableFloorplanPricing}
          utilityAllowanceId={utilityAllowanceId}
          renewalLeaseStartDate={props?.initialValues?.renewalLeaseStartDate}
          renewalIsRenewalComplete={
            props?.initialValues?.renewalIsRenewalComplete
          }
          receivingAssistanceUpdate={(assistanceValue) => {
            handleReceivingAsstSubmit(assistanceValue);
          }}
          refreshLDT={(_assistanceState, newLeasedRent) => {
            refreshData();
            updateTransactionsList();
            if (newLeasedRent != null) {
              change('leasedRent', newLeasedRent);
            }
            if (residentId) {
              actions.getOneResident(residentId);
            } else if (applicationId) {
              actions.getOneApplication(applicationId);
            }
          }}
          isHUDorRD={isHUDFloorPlan || isRDFloorPlan}
        />
      )}
      <EditLeaseDataModal
        show={modal === 'editLeaseDataModal'}
        onSubmit={handleLeaseDataModalSubmit}
        dismiss={hideModals}
        leaseTerms={editModalLeaseTerms}
        leaseRentPercentage={leaseRentPercentage}
        quotingRent={quotingRent}
        initialValues={editLeaseDataModalInitial}
        unit={unit}
        intl={intl}
        receivingAssistance={receivingAssistance}
        affordableValues={affordableValues}
        applicableRents={applicableRents}
        lease={lease}
      />
      <EditRequiredLeaseFeesModal
        intl={intl}
        show={modal === 'editLeaseRequiredFeesModal'}
        initialValues={{ monthlyReqFees: editLeaseRequiredFeesInitial }}
        dismiss={hideModals}
        leaseRequiredFees={monthlyRecurringCharges}
        leaseIsSaved={!!lease.id}
        handleSaveFees={handleLeaseRequiredFeesSubmitCurried}
        isSubmitting={isSubmittingUpdateFees}
      />

      <LeaseBasics
        basicLeaseFees={basicLeaseFees}
        canComplete={canComplete}
        currentLeaseSelected={currentLeaseSelected}
        formValues={formValues || {}}
        intl={intl}
        isEditable={isEditable}
        labelEndDate={labelEndDate}
        lateMethods={lateMethods}
        lease={lease}
        residentId={residentId}
        transferInformation={transferInformation}
        showLeaseDataModal={showLeaseDataModal}
        unit={unit}
        leaseTerms={leaseBasicsLeaseTerms}
        updateLeaseDate={updateLeaseDate}
        formAsyncErrors={formAsyncErrors}
        selectedProperty={selectedProperty}
        promptToaster={actions.promptToaster}
        noticeToVacate={noticeToVacate}
        setProratedRentAmount={setProratedRentAmount}
        setProratedOtherAmount={setProratedOtherAmount}
        resetProratedRentAutoFilledChangeStatus={
          resetProratedRentAutoFilledChangeStatus
        }
        monthlyRecurringCharges={monthlyRecurringCharges}
        onPendingCheckUpdate={setPendingMoveInCheckState}
        householdId={householdId}
        householdRentableItems={householdRentableItems}
        petCount={petCount}
        isShortTermLease={isShortTermLease}
        notifyTrackedInputChanged={notifyTrackedInputChanged}
      />

      <Row>
        {/* Lease rent uses isEditable, not autoPopulatedFieldsAreEditable (leasedRent is an exception). */}
        <MonthlyLeaseCharges
          monthlyRecurringCharges={activeMonthlyRecurringFees}
          intl={intl}
          isAffordable={affordableValues.isAffordable}
          isEditable={isEditable}
          leaseRentIsEditable={isEditable}
          marketRent={marketRent}
          leaseRentPercentage={leaseRentPercentage}
          nonRentCharges={applicableNonRentCharges}
          quotingRent={quotingRent}
          recommendedRentProviderName={recommendedRentProviderName}
          clearRecommendedRent={clearRecommendedRent}
          noteRent={noteRent}
          basicRent={basicRent}
          affordableValues={affordableValues}
          applicableRents={applicableRents}
          showEditLink={!!(residentId && currentLeaseSelected)}
          showLeaseDataModal={
            flags?.automateMonthlyLeaseTransactionsMvp
              ? openModal
              : showLeaseDataModal
          }
          showLeaseReqFeesModal={showLeaseReqFeesModal}
          isDisabledMonthly={isDisabledMonthly}
          lease={lease}
          canComplete={canComplete}
          changeFn={change}
          selectedProperty={selectedProperty}
          nonOptionalCharge={nonOptionalCharge}
          isSection236={isSection236}
          isHUD={isHUDFloorPlan}
          isRD={isRDFloorPlan}
          isPH={isPHFloorPlan}
          hud236BasicRent={isSection236 ? hud236BasicRent : null}
          hud236MarketRent={isSection236 ? hud236MarketRent : null}
          formValues={formValues || {}}
          setProratedRentAmount={setProratedRentAmount}
          setProratedOtherAmount={setProratedOtherAmount}
          resetProratedRentAutoFilledChangeStatus={
            resetProratedRentAutoFilledChangeStatus
          }
          householdRentableItems={householdRentableItems}
          petCount={petCount}
          isShortTermLease={isShortTermLease}
        />
        <Reconciliation
          formValues={formValues}
          difference={difference}
          intl={intl}
          nonRentCharges={applicableNonRentCharges}
          totalMonthlyCharges={totalMonthlyCharges}
        />
      </Row>
      <div className="divider" />
      <PetFeesAndRent
        intl={intl}
        isEditable={autoPopulatedFieldsAreEditable}
        handleAutoFilledFieldChange={handleAutoFilledFieldChange}
        autoFilledLeaseChargeFields={autoFilledLeaseChargeFields}
      />
      <div className="divider" />
      <OneTimeLeaseChargesOrCredits
        intl={intl}
        isEditable={isEditable}
        autoPopulatedFieldsAreEditable={autoPopulatedFieldsAreEditable}
        handleAutoFilledFieldChange={handleAutoFilledFieldChange}
        securityDeposits={securityDeposits}
        autoFilledLeaseChargeFields={autoFilledLeaseChargeFields}
      />
      <Concessions intl={intl} isEditable={isEditable} />
      <EmergencyContactInformation intl={intl} isEditable={isEditable} />
      <div className="divider" />
      <MonthlyLeaseChargesOrCredits
        applicationId={applicationId}
        canComplete={canComplete}
        frNames={frNames}
        intl={intl}
        isDisabledMonthly={isDisabledMonthly}
        isLocked={lease.isLocked}
        labelEndDate={labelEndDate}
        lease={lease}
        formValues={formValues}
        residentId={residentId}
        selectedMonthlyOption={selectedMonthlyOption}
        valid={valid}
        showGenerateMessage={!residentId || lease.isRenewal || lease.isTransfer}
        monthlyTransactions={monthlyTransactions}
      />
      <div className="divider" />
      {shouldMountLeasePacket && (
        // `contents` adds no box, so a shown section lays out as before. Hidden keeps it mounted.
        <div
          style={{ display: shouldDisplayLeasePacket ? 'contents' : 'none' }}
        >
          <LeasePacket
            leaseId={lease?.id}
            draftScope={residentId ?? applicationId}
            isLeaseLocked={lease?.isLocked}
            isReadOnly={isLeasePacketReadOnly}
            isAffordable={floorPlan?.isAffordable}
            isCommercial={floorPlan?.isCommercial}
          />
          <div className="divider" />
        </div>
      )}
      <LeaseSigningMethod
        hasNonFR={hasNonFR}
        isEditable={isEditable}
        lease={lease}
        leaseProvider={leaseProvider}
        selectedProperty={selectedProperty}
        formSigMethod={formValues?.desiredSignatureMethod}
        initialSigMethod={props?.initialValues?.desiredSignatureMethod}
        onRestoreSigMethod={
          flags?.blueMoonLeaseSigning || blueMoonSeen
            ? restoreSigMethod
            : undefined
        }
      />
      {(!residentId || lease.isRenewal || lease.isTransfer) && (
        <LeaseActionButtons
          allAffordableTabsApproved={allAffordableTabsApproved}
          unapprovedAffordablePrograms={unapprovedAffordablePrograms}
          moveInCertBlocked={moveInCertBlocked}
          moveInCertTooltip={moveInCertTooltip}
          applicationId={applicationId}
          canComplete={canComplete}
          difference={difference}
          handleSubmit={handleSubmit}
          hasNonFR={hasNonFR}
          intl={intl}
          isRenewalComplete={isRenewalComplete}
          lease={lease}
          leaseProvider={leaseProvider}
          providerIsUsable={providerIsUsable}
          leaseSignatureStatuses={leaseSignatureStatuses}
          onGenerate={onGenerate ? handleSubmit(onGenerate) : null}
          onSubmit={handleFormSubmit}
          prospectId={prospectInfo.id}
          residentId={residentId}
          submitting={submitting}
          asyncValidating={asyncValidating}
          requiredFeesUpdated={requiredFeesUpdated}
          transferCertBlocked={transferCertBlocked}
          transferCertTooltip={transferCertTooltip}
          disablePendingMoveInCheck={
            pendingMoveInCheckState.isChecking ||
            pendingMoveInCheckState.hasError
          }
        />
      )}
    </React.Fragment>
  );
};

export const mapStateToProps = (state: GlobalState): Object => {
  const { app } = state;

  return {
    formSyncWarnings: getFormSyncWarnings('FormLeaseDataTab')(state),
    formValues: getFormValues('FormLeaseDataTab')(state),
    editFormValues: getFormValues('editLeaseDataForm')(state),
    editLeaseFeesFormValues: getFormValues('editRequiredLeaseFeesForm')(state),
    selectedProperty: app.selectedProperty,
    formAsyncErrors: getFormAsyncErrors('FormLeaseDataTab')(state),
    formSyncErrors: getFormSyncErrors('FormLeaseDataTab')(state),
  };
};

export function mapDispatchToProps(dispatch: any): Object {
  const actions = bindActionCreators(
    {
      promptToaster,
      getOneResident,
      getOneApplication,
    },
    dispatch,
  );
  return { actions };
}
export default withLDConsumer()(
  connect(
    mapStateToProps,
    mapDispatchToProps,
  )(
    withExpirationLimitsPropertyToggle(
      reduxForm({
        form: 'FormLeaseDataTab',
        enableReinitialize: true,
        validate,
        warn,
        asyncValidate: asyncValidate(),
        asyncChangeFields: [
          'overrideLeaseExpirationLimit',
          'endDate',
          'startDate',
          'leaseTermId',
        ],
        asyncBlurFields: [
          'overrideLeaseExpirationLimit',
          'endDate',
          'startDate',
          'leaseTermId',
        ],
      })(LeaseDataTabForm),
    ),
  ),
);
